import { useEffect, useRef, useState } from "react";
import { currentRecording, shell, startRecording, stopRecording } from "./recording-shell";
import { Link } from "./router";

/**
 * The button that makes the applications worth installing.
 *
 * A browser cannot record a fifty-minute talk with the screen locked, so this is drawn
 * only inside the installed application. On the website `shell()` answers null, nothing
 * renders, and a teacher is never offered something that would not work.
 *
 * It is also drawn only when the presenter allowed recording. She is never told that a
 * presenter declined — the presence of the button is the entire message, and the
 * application does not explain recording policy to her.
 */
export function RecordSessionControl({ itemId, title, allowed, signedIn }: {
  itemId: string;
  title: string;
  allowed: boolean;
  /**
   * Whether anybody is signed in on this device.
   *
   * Load-bearing rather than cosmetic. A recording is reported and uploaded with the
   * session cookie, so a signed-out phone records fifty minutes, tries to send it, and is
   * refused — the audio stays on the phone and WVCEA never learns the session happened.
   * She would have no way of knowing. Better to ask her to sign in first.
   */
  signedIn: boolean;
}) {
  const [inShell, setInShell] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [finished, setFinished] = useState<number | null>(null);
  const startedAt = useRef<number | null>(null);

  // Asked on every mount rather than remembered, because the page reloads whenever a
  // teacher switches tabs and the phone may have been recording the whole time.
  useEffect(() => {
    if (!allowed || !signedIn || !shell()) return;
    setInShell(true);
    let cancelled = false;
    void currentRecording().then((running) => {
      if (cancelled || running !== itemId) return;
      setRecording(true);
      startedAt.current = Date.now();
    });
    return () => { cancelled = true; };
  }, [allowed, signedIn, itemId]);

  // The timer is drawn from the clock rather than counted up, so a screen that slept for
  // twenty minutes shows twenty minutes rather than the handful of ticks it stayed awake
  // for.
  useEffect(() => {
    if (!recording) return;
    const tick = () => {
      if (startedAt.current) setSeconds(Math.floor((Date.now() - startedAt.current) / 1000));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  if (!allowed || !inShell) return null;

  // Recording without a session would be recording into nothing. Said plainly, because
  // the alternative is a button that works and then loses her session silently.
  if (!signedIn) {
    return (
      <section className="session-record" aria-label="Record this session">
        <p className="session-record-locked">
          Sign in to record this session. The audio and its transcript go into your own
          notes, so they need somewhere to go.
        </p>
        <Link className="session-record-start" href="/account">Sign in</Link>
      </section>
    );
  }

  const begin = async () => {
    setBusy(true);
    setProblem(null);
    setFinished(null);
    const result = await startRecording(itemId, title);
    setBusy(false);
    if (!result.started) {
      setProblem(result.problem ?? "Recording could not be started.");
      return;
    }
    startedAt.current = Date.now();
    setSeconds(0);
    setRecording(true);
  };

  const end = async () => {
    setBusy(true);
    const result = await stopRecording();
    setBusy(false);
    setRecording(false);
    startedAt.current = null;
    if (result.problem) setProblem(result.problem);
    // What happens next — transcribing, and whether WVCEA wants this copy — happens
    // behind her and is deliberately not reported. She is walking out of a room.
    if (result.stopped) setFinished(result.recordedSeconds);
  };

  return (
    <section className="session-record" aria-label="Record this session">
      {recording ? (
        <>
          <p className="session-record-live" role="status">
            <span className="session-record-dot" aria-hidden="true" />
            Recording · {clock(seconds)}
          </p>
          <button type="button" className="session-record-stop" disabled={busy} onClick={() => void end()}>
            {busy ? "Stopping…" : "Stop recording"}
          </button>
          <small>You can lock your phone. Recording carries on.</small>
        </>
      ) : (
        <>
          <button type="button" className="session-record-start" disabled={busy} onClick={() => void begin()}>
            <MicrophoneIcon />
            <span>{busy ? "Starting…" : "Record this session"}</span>
          </button>
          {finished === null
            ? <small>The audio and a written transcript go into your notes.</small>
            : <small>Saved · {clock(finished)} recorded. It is in your notes.</small>}
        </>
      )}
      {problem && <p className="note-storage-error" role="alert">{problem}</p>}
    </section>
  );
}

/**
 * Drawn here rather than added to the shared icon set, which has no microphone and is
 * defined inside App.tsx. One glyph used in one place does not need to be exported.
 */
function MicrophoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor">
      <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
      <path d="M17 11a1 1 0 0 0-2 0 3 3 0 0 1-6 0 1 1 0 0 0-2 0 5 5 0 0 0 4 4.9V19H9a1 1 0 0 0 0 2h6a1 1 0 0 0 0-2h-2v-3.1A5 5 0 0 0 17 11z" />
    </svg>
  );
}

/** Minutes and seconds, and hours only once there is an hour to show. */
function clock(total: number): string {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
